import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes,
  FaCalendarAlt
} from 'react-icons/fa';
import { 
  getProjectMeetingNoteTypes, 
  createProjectMeetingNoteTypes, 
  updateProjectMeetingNoteType, 
  deleteProjectMeetingNoteType 
} from '../../api/meetingNotes';

interface MeetingTypesManagerProps {
  token: string | undefined;
  onClose?: () => void;
  isModal?: boolean;
}

export const MeetingTypesManager = ({ token, onClose, isModal = true }: MeetingTypesManagerProps) => {
  const [meetingTypes, setMeetingTypes] = useState<ProjectMeetingNoteTypesInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Load meeting types
  const loadMeetingTypes = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await getProjectMeetingNoteTypes(token);
      setMeetingTypes(response.results);
    } catch (error) {
      toast.error('Failed to load meeting types');
      console.error('Error loading meeting types:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeetingTypes();
  }, [token]);

  // Create new meeting type
  const handleCreate = async () => {
    if (!token || !newTypeName.trim()) return;

    try {
      const newType = {
        id: crypto.randomUUID(),
        name: newTypeName.trim()
      };

      await createProjectMeetingNoteTypes(token, newType);
      await loadMeetingTypes();
      setNewTypeName('');
      setIsCreating(false);
      toast.success('Meeting type created successfully');
    } catch (error) {
      toast.error('Failed to create meeting type');
      console.error('Error creating meeting type:', error);
    }
  };

  // Update meeting type
  const handleUpdate = async (id: string, name: string) => {
    if (!token || !name.trim()) return;

    try {
      const updatedType = {
        id,
        name: name.trim()
      };

      await updateProjectMeetingNoteType(token, id, updatedType);
      await loadMeetingTypes();
      setEditingId(null);
      setEditingName('');
      toast.success('Meeting type updated successfully');
    } catch (error) {
      toast.error('Failed to update meeting type');
      console.error('Error updating meeting type:', error);
    }
  };

  // Delete meeting type
  const handleDelete = async (id: string, name: string) => {
    if (!token) return;

    if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteProjectMeetingNoteType(token, id);
      await loadMeetingTypes();
      toast.success('Meeting type deleted successfully');
    } catch (error) {
      toast.error('Failed to delete meeting type');
      console.error('Error deleting meeting type:', error);
    }
  };

  const startEditing = (type: ProjectMeetingNoteTypesInterface) => {
    setEditingId(type.id);
    setEditingName(type.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const content = (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
        <div className="flex items-center space-x-3">
          <FaCalendarAlt className="w-5 h-5 text-[#888]" />
          <h2 className="text-xl font-semibold font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">
            Meeting Types
          </h2>
        </div>
        {isModal && onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#fafafa] rounded-md transition-colors"
          >
            <FaTimes className="w-4 h-4 text-[#888]" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Create New Type */}
        <div className="mb-6">
          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#171717] transition-colors"
            >
              <FaPlus className="w-4 h-4" />
              <span>Add New Meeting Type</span>
            </button>
          ) : (
            <div className="flex items-center space-x-3 p-4 border border-[#eaeaea] rounded-md bg-[#f5f5f5]">
              <input
                type="text"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Enter meeting type name..."
                className="flex-1 px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreate();
                  } else if (e.key === 'Escape') {
                    setIsCreating(false);
                    setNewTypeName('');
                  }
                }}
              />
              <button
                onClick={handleCreate}
                disabled={!newTypeName.trim()}
                className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:bg-[#ddd] disabled:cursor-not-allowed transition-colors"
              >
                <FaSave className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewTypeName('');
                }}
                className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#555] transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Meeting Types List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#888]"></div>
            <span className="ml-3 text-[#555]">Loading meeting types...</span>
          </div>
        ) : meetingTypes.length === 0 ? (
          <div className="text-center py-8 text-[#888]">
            <FaCalendarAlt className="w-12 h-12 mx-auto mb-4 text-[#ccc]" />
            <p>No meeting types found</p>
            <p className="text-sm">Create your first meeting type to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {meetingTypes.map((type) => (
              <div
                key={type.id}
                className="flex items-center justify-between p-4 border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors"
              >
                {editingId === type.id ? (
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdate(type.id, editingName);
                        } else if (e.key === 'Escape') {
                          cancelEditing();
                        }
                      }}
                    />
                    <button
                      onClick={() => handleUpdate(type.id, editingName)}
                      disabled={!editingName.trim()}
                      className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:bg-[#ddd] disabled:cursor-not-allowed transition-colors"
                    >
                      <FaSave className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#555] transition-colors"
                    >
                      <FaTimes className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="font-medium text-[#171717]">{type.name}</span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startEditing(type)}
                        className="p-2 text-[#888] hover:bg-[#f5f5f5] rounded-md transition-colors"
                        title="Edit"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(type.id, type.name)}
                        className="p-2 text-red-500 hover:bg-red-100 rounded-md transition-colors"
                        title="Delete"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
          {content}
        </div>
      </div>
    );
  }

  return content;
}; 